import { parseIntegerInput } from "@/lib/parse-number";
import type {
  DocBlock,
  DocListStyle,
  DocRun,
  DocText,
} from "@/lib/tool-kit/document";

/**
 * Reading what a writer typed into a template form.
 *
 * The essay-template forms are textareas, and a textarea carries two kinds of answer:
 * prose ("Describe what happened") and lists ("Materials — one per line"). Both arrive
 * as one string. These helpers turn them into document blocks and fall back to a prompt
 * while the field is empty, which is what makes every template useful before a single
 * field is filled in: the empty form is already a complete skeleton with guidance.
 */

const LINE_BREAK = /\r?\n/;
/** A bullet or a number a writer pasted in front of a line: "- ", "• ", "3. ", "2) ". */
const LIST_PREFIX = /^(?:[-*•–—·]|\d+[.)])\s+/;
const WORD = /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu;
const FIRST_WORD = /^\p{L}+/u;
const SEMICOLON_LIST = /[;\n]/;
const COMMA_LIST = /[,;\n]/;
const LEAD_IN_COLON = /:\s*/;
const LEAD_IN_DASH = /\s+[–—-]\s+/;
const PLACEHOLDER = /\{(\w+)\}/g;
const SPACES = /\s/g;
const GROUPED_COUNT = /^\d{1,3}([.,])\d{3}(?:\1\d{3})*$/;
const SEPARATORS = /[.,]/g;

/**
 * A whole-number count: a word or page total. The site's number parser reads a lone
 * separator as a decimal mark — "3,000" is 3, the way German writes 3.0 — which is right
 * for a measurement and wrong for a count, since nobody plans a three-word proposal. A
 * count cannot be fractional, so a correctly grouped "3,000" or "3.000" is read as
 * thousands; anything else goes through `parseIntegerInput`.
 */
export function parseWholeCount(raw: string): number | null {
  const compact = raw.replace(SPACES, "");
  if (GROUPED_COUNT.test(compact)) {
    return Number(compact.replace(SEPARATORS, ""));
  }
  return parseIntegerInput(raw);
}

/**
 * "Wilbur — a young pig" → ["Wilbur", "a young pig"]; "Months 1–3: Ethics approval" →
 * ["Months 1–3", "Ethics approval"]. A colon wins over a dash, and a dash only counts
 * with spaces round it, so the "1–3" of a range never splits. Null when there is no
 * separator, or nothing before it.
 */
export function splitLeadIn(line: string): [string, string] | null {
  const match = LEAD_IN_COLON.exec(line) ?? LEAD_IN_DASH.exec(line);
  if (!match || match.index === 0) {
    return null;
  }
  return [
    line.slice(0, match.index).trim(),
    line.slice(match.index + match[0].length).trim(),
  ];
}

/**
 * `t()` for a document line whose interpolated values need emphasis — "Book Report:
 * {title}" with the title in italics. Literal text stays plain; each value named in
 * `italicKeys` becomes an italic run. An unknown placeholder is left as written.
 */
export function interpolateRuns(
  template: string,
  values: Record<string, string>,
  italicKeys: readonly string[] = []
): DocRun[] {
  const runs: DocRun[] = [];
  let cursor = 0;
  for (const match of template.matchAll(PLACEHOLDER)) {
    const [whole, key] = match;
    const index = match.index ?? 0;
    if (!(key in values)) {
      continue;
    }
    if (index > cursor) {
      runs.push({ text: template.slice(cursor, index) });
    }
    runs.push(
      italicKeys.includes(key)
        ? { text: values[key], italic: true }
        : { text: values[key] }
    );
    cursor = index + whole.length;
  }
  if (cursor < template.length) {
    runs.push({ text: template.slice(cursor) });
  }
  return runs;
}

/** "Wilbur — a young pig" as a list item with the name in bold. */
export function leadInItem(line: string): DocRun[] {
  const parts = splitLeadIn(line);
  if (!parts) {
    return [{ text: line }];
  }
  return [{ text: parts[0], bold: true }, { text: ` — ${parts[1]}` }];
}

/** The writer's lines, trimmed, with blank lines dropped. */
export function splitLines(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/**
 * One list item per line. A pasted "1." or "-" is dropped, since the template numbers
 * its own lists — keeping it would print "1. 1. Fill the beaker".
 */
export function splitItems(text: string): string[] {
  return splitLines(text)
    .map((line) => line.replace(LIST_PREFIX, "").trim())
    .filter((line) => line !== "");
}

/**
 * Short values typed on one line: "10, 20, 30 cm". Same rule as the site's number lists
 * (`parse-number.ts`): a semicolon anywhere makes it the separator, which frees the comma
 * to be a decimal mark in "2,5; 5,0; 7,5".
 */
export function splitValues(text: string): string[] {
  const separator = text.includes(";") ? SEMICOLON_LIST : COMMA_LIST;
  return text
    .split(separator)
    .map((value) => value.trim())
    .filter((value) => value !== "");
}

export function countWords(text: string): number {
  return text.match(WORD)?.length ?? 0;
}

/** "oxygen production" → "Oxygen production", for a fragment that opens a caption. */
export function capitalizeFirst(text: string, locale: string): string {
  const trimmed = text.trim();
  if (trimmed === "") {
    return trimmed;
  }
  return trimmed.charAt(0).toLocaleUpperCase(locale) + trimmed.slice(1);
}

/**
 * "Complete two problem sets" → "complete two problem sets", for a fragment that
 * continues a sentence ("I will …"). Only a capitalised ordinary word is lowered:
 * "APA", "I" and "iPhone" are left as typed. A proper noun at the start ("Python") is
 * lowered too — the one case the rule cannot see — which is why the tools that use this
 * say to check the wording.
 */
export function lowerFirstWord(text: string, locale: string): string {
  const trimmed = text.trim();
  const word = FIRST_WORD.exec(trimmed)?.[0] ?? "";
  if (word.length < 2) {
    return trimmed;
  }
  const rest = word.slice(1);
  const isCapitalised =
    word.charAt(0) !== word.charAt(0).toLocaleLowerCase(locale) &&
    rest === rest.toLocaleLowerCase(locale);
  if (!isCapitalised) {
    return trimmed;
  }
  return trimmed.charAt(0).toLocaleLowerCase(locale) + trimmed.slice(1);
}

/** The writer's paragraphs — every line is one — or a prompt while the field is empty. */
export function paragraphsOrPrompt(text: string, prompt: DocText): DocBlock[] {
  const paragraphs = splitLines(text);
  if (paragraphs.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  return paragraphs.map((paragraph) => ({
    kind: "paragraph",
    text: paragraph,
  }));
}

/** The writer's list, one item per line, or a prompt while the field is empty. */
export function listOrPrompt(
  text: string,
  style: DocListStyle,
  prompt: DocText
): DocBlock[] {
  const items = splitItems(text);
  if (items.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  return [
    {
      kind: "list",
      list: { style, items: items.map((item) => ({ text: item })) },
    },
  ];
}

/** A labelled value when the writer gave one, otherwise a prompt saying what belongs there. */
export function fieldOrPrompt(
  label: string,
  value: string,
  prompt: DocText
): DocBlock {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { kind: "prompt", text: prompt };
  }
  return { kind: "fields", fields: [{ label, value: trimmed }] };
}
